import { useCallback, useEffect, useRef, useState } from "react";
import { openDailyCloseReport } from "../../../report/dailyClosingReport";
import {
  GET_DAILY_CLOSINGS,
  UPDATE_CONSULTORY_MONTHLY_CLOSURE,
} from "./queries";
import { useApolloClient, useMutation, useQuery } from "@apollo/react-hooks";
import moment from "moment";
import { isEmpty } from "lodash";
import { useToasts } from "react-toast-notifications";
import { getGraphQLErrorMessage } from "@dhn/shared/utils";
import { DAILY_CLOSING_GET_ALL } from "../../../appointment-admin/Modals/DailyClosingModal/queries";
import { useParams } from "react-router";
import { openMonthlyClosingReport } from "../../reports/monthlyClosingReport";
import { POPUP_BLOCKED_MESSAGE, tryOpenReport } from "../../reports/openReport";
import {
  getBillingReportWhere,
  getLogoDataUrl,
  printNcfPrincipalReport,
} from "../../reports/reportContext";
import {
  GET_BILLINGS_BY_PERIOD,
  GET_MONTHLY_CLOSING_BY_ID,
} from "../MonthlyClosingDetail/queries";

const INITIAL_STATE = {
  data: [],
  isProcessing: false,
};

export default function useActions({ history, dataDoctor, user }) {
  const [state, setState] = useState(INITIAL_STATE);
  const processingRef = useRef(false);
  const client = useApolloClient();
  const { addToast } = useToasts();
  const { params } = useParams();
  const monthlyClosingId = parseInt(params);
  const [
    updateConsultoryMonthlyClosure,
    { loading: loadingUpdateMonthlyClosing },
  ] = useMutation(UPDATE_CONSULTORY_MONTHLY_CLOSURE);

  const {
    data: { getDailyClosings } = {},
    loading: loadingDailyClosings,
    refetch: refetchDailyClosings,
  } = useQuery(GET_DAILY_CLOSINGS, {
      skip:
        !dataDoctor?.medicalStaffId ||
        !dataDoctor?.branchOfficeId ||
        Number.isNaN(monthlyClosingId),
      fetchPolicy: "network-only",
      variables: {
        where: {
          medicalStaffId: dataDoctor?.medicalStaffId,
          branchOfficeId: dataDoctor?.branchOfficeId,
          monthlyClosingId,
        },
        relations: ["status", "user"],
      },
    });

  const { data: { getConsultoryMonthlyClosure } = {} } = useQuery(
    GET_MONTHLY_CLOSING_BY_ID,
    {
      skip: Number.isNaN(monthlyClosingId) || !dataDoctor?.medicalStaffId,
      fetchPolicy: "network-only",
      variables: {
        where: {
          id: monthlyClosingId,
          medicalStaffId: dataDoctor?.medicalStaffId,
          branchOfficeId: dataDoctor?.branchOfficeId,
        },
        relations: ["pharmacyProvider"],
      },
    },
  );

  const monthlyClosing = getConsultoryMonthlyClosure?.[0];

  const getClosingUserName = useCallback(
    (dailyClosing = {}) => {
      const fullName =
        `${dailyClosing?.user?.firstName || ""} ${dailyClosing?.user?.lastName || ""}`.trim();

      if (fullName) {
        return fullName;
      }

      if (dailyClosing?.user?.username) {
        return dailyClosing.user.username;
      }

      return `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
    },
    [user?.firstName, user?.lastName],
  );

  const handleSuma = useCallback((array = [], key) => {
    return array.reduce((acc, item) => {
      return acc + parseFloat(item[key] || 0);
    }, 0);
  }, []);

  const handleShowReportDetail = useCallback(
    async (dataSelected) => {
      if (dataSelected?.empty) {
        addToast("No hay datos para mostrar el reporte", {
          appearance: "warning",
          autoDismiss: true,
        });
        return;
      }

      try {
        const selectedDate = dataSelected?.date
          ? moment(dataSelected.date).format("YYYY-MM-DD")
          : null;

        const { data: { getDailyClosingSummary } = {} } = await client.query({
          query: DAILY_CLOSING_GET_ALL,
          fetchPolicy: "network-only",
          variables: {
            where: {
              medicalStaffId: dataSelected?.medicalStaffId,
              branchOfficeId: dataSelected?.branchOfficeId,
              date: selectedDate,
              dateOfCutNull: false,
            },
          },
        });

        const { byPaymentMethod, byArs } = getDailyClosingSummary ?? {};

        if (isEmpty(byPaymentMethod) && isEmpty(byArs)) {
          addToast("No hay datos para mostrar el reporte", {
            appearance: "warning",
            autoDismiss: true,
          });
          return;
        }

        const opened = tryOpenReport(openDailyCloseReport, {
          logoDataUrl: getLogoDataUrl(),
          date: dataSelected?.closeDate || dataSelected?.date,
          centerName: dataDoctor?.centerName,
          doctorName: dataDoctor?.fullName,
          userName: getClosingUserName(dataSelected),
          insuranceSales: byArs || [],
          coPayments: byPaymentMethod || [],
          handleValueModeOfSale: () => handleSuma(byArs, "coverage"),
          handleValueDiferencia: () =>
            handleSuma(
              (byPaymentMethod || []).map((item) => ({
                value: item?.diferencia ?? item?.copago ?? 0,
              })),
              "value",
            ),
        });

        if (!opened) {
          addToast(POPUP_BLOCKED_MESSAGE, {
            appearance: "warning",
            autoDismiss: true,
          });
        }
      } catch (error) {
        addToast(
          getGraphQLErrorMessage(
            error,
            "No fue posible cargar el detalle del cierre diario.",
          ),
          {
            appearance: "error",
            autoDismiss: true,
          },
        );
      }
    },
    [
      addToast,
      client,
      dataDoctor?.centerName,
      dataDoctor?.fullName,
      getClosingUserName,
      handleSuma,
    ],
  );

  // Imprime los NCF por ARS; un fallo aqui no revierte el cierre ya procesado.
  const printMonthlyClosingNcfReports = useCallback(
    async (logoDataUrl) => {
      const {
        data: { getConsultoryMonthlyClosure: closings = [] } = {},
      } = await client.query({
        query: GET_MONTHLY_CLOSING_BY_ID,
        fetchPolicy: "network-only",
        variables: {
          where: {
            id: monthlyClosingId,
            medicalStaffId: dataDoctor?.medicalStaffId,
            branchOfficeId: dataDoctor?.branchOfficeId,
          },
          relations: ["pharmacyProvider"],
        },
      });

      const {
        data: { getConsultoryMonthlyClosedDetail = [] } = {},
      } = await client.query({
        query: GET_BILLINGS_BY_PERIOD,
        fetchPolicy: "network-only",
        variables: {
          where: {
            monthlyClosingId,
            medicalStaffId: dataDoctor?.medicalStaffId,
            branchOfficeId: dataDoctor?.branchOfficeId,
          },
        },
      });

      const result = { printed: 0, blocked: 0 };

      for (const billing of getConsultoryMonthlyClosedDetail.filter(
        (item) => item?.firstSaleId,
      )) {
        const opened = await printNcfPrincipalReport(client, {
          monthlyClosing: closings?.[0],
          billing,
          logoDataUrl,
          where: getBillingReportWhere({ monthlyClosingId, dataDoctor, billing }),
        });

        result[opened ? "printed" : "blocked"] += 1;
      }

      return result;
    },
    [client, dataDoctor, monthlyClosingId],
  );

  const handleShowOpenMonthlyClosingReport = useCallback(
    async ({ getDaysOfMonth }) => {
      if (processingRef.current) {
        return;
      }

      processingRef.current = true;
      setState((prev) => ({ ...prev, isProcessing: true }));

      let isClosed = false;

      try {
        const logoDataUrl = getLogoDataUrl();
        let fiscalSummary = {};

        try {
          const mutationResult = await updateConsultoryMonthlyClosure({
            variables: {
              data: {
                id: monthlyClosingId,
                statusId: 3,
              },
            },
          });

          fiscalSummary =
            mutationResult?.data?.updateConsultoryMonthlyClosure
              ?.fiscalSummary || {};
          isClosed = true;
        } catch (error) {
          addToast(
            getGraphQLErrorMessage(
              error,
              "No fue posible procesar el cierre mensual.",
            ),
            {
              appearance: "error",
              autoDismiss: true,
            },
          );
          return;
        }

        let blockedReports = tryOpenReport(openMonthlyClosingReport, {
          logoDataUrl,
          getDaysOfMonth,
          centerName: dataDoctor?.centerName,
          doctorName: dataDoctor?.fullName,
          date: new Date(),
          fiscal: {
            insuredNCF: fiscalSummary?.insuredFAC || [],
            copayNCF: fiscalSummary?.copayB02 || [],
            otherNCF: fiscalSummary?.ncfInvoices || [],
          },
        })
          ? 0
          : 1;

        let printedCount = 0;

        try {
          const { printed, blocked } = await printMonthlyClosingNcfReports(
            logoDataUrl,
          );
          printedCount = printed;
          blockedReports += blocked;
        } catch (error) {
          addToast(
            `El cierre se proceso, pero no fue posible generar los reportes NCF: ${getGraphQLErrorMessage(
              error,
            )} Puede reimprimirlos desde el detalle del cierre.`,
            {
              appearance: "warning",
              autoDismiss: true,
            },
          );
        }

        addToast(
          printedCount > 0
            ? `Cierre mensual procesado correctamente. Se generaron ${printedCount} reporte(s) NCF por ARS.`
            : "Cierre mensual procesado correctamente",
          {
            appearance: "success",
            autoDismiss: true,
          },
        );

        if (blockedReports > 0) {
          addToast(
            `${POPUP_BLOCKED_MESSAGE} Los reportes pueden reimprimirse desde el detalle del cierre.`,
            {
              appearance: "warning",
              autoDismiss: true,
            },
          );
        }
      } finally {
        processingRef.current = false;
        setState((prev) => ({ ...prev, isProcessing: false }));
      }

      if (isClosed) {
        history.push("/monthly-closing");
      }
    },
    [
      dataDoctor?.centerName,
      dataDoctor?.fullName,
      updateConsultoryMonthlyClosure,
      monthlyClosingId,
      printMonthlyClosingNcfReports,
      addToast,
      history,
    ],
  );

  useEffect(() => {
    if (!loadingDailyClosings && getDailyClosings) {
      setState((prev) => ({
        ...prev,
        data: getDailyClosings || [],
      }));
    }
  }, [loadingDailyClosings, getDailyClosings]);

  useEffect(() => {
    const refreshData = () => {
      refetchDailyClosings();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshData();
      }
    };

    window.addEventListener("focus", refreshData);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("focus", refreshData);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [refetchDailyClosings]);

  return [
    { state, loadingDailyClosings, monthlyClosing },
    {
      handleShowReportDetail,
      handleShowOpenMonthlyClosingReport,
      loadingUpdateMonthlyClosing:
        loadingUpdateMonthlyClosing || state.isProcessing,
    },
  ];
}
